<template>
  <!-- removing 'theming classes' will cause style issue throughout the site -->
  <main ref="mainWrap" class="tw-w-full">
    <modio-toast />
    <div
      ref="container"
      class="tw-flex tw-flex-col tw-h-full tw-w-full tw-relative tw-overflow-x-hidden tw-text-theme"
      :style="{ minHeight: `${MIN_PAGE_HEIGHT}px` }"
    >
      <router-view
        v-if="!requireAccess"
        :key="$route.path"
        v-slot="{ Component }"
      >
        <component :is="Component"></component>
      </router-view>
    </div>
    <global-modals />
  </main>
</template>

<script>
import { sendMessage, containerHeight } from '@composables/useWindowEvents.js'
import { onMounted, nextTick, ref, computed, watch } from 'vue'
import ViewContainer from '@components/View/ViewContainer.vue'
import { authStore, localModStore, gameStore } from '@stores'
import { GAME_ID, HEADERS, sdkPlatform } from '@config/game.js'
import ModioToast from '@components/Message/ModioToast.vue'
import GlobalModals from '@views/layouts/GlobalModals.vue'
import { setGlobalModal } from '@composables/useModal.js'
import { STATUS_NOT_ACCEPTED } from '@config/options.js'
import * as sdkInterface from '@plugins/sdkInterface.js'
import { MODAL_LOGIN } from '@config/globalModals.js'
import webInterface from '@plugins/webInterface.js'
import { updateLocalStorage } from '@services'
import { MIN_PAGE_HEIGHT } from '@config'
import {
  useWindowEvents,
  useBoundaries,
  useTheming,
  useSSO,
} from '@composables'
export default {
  components: {
    ViewContainer,
    GlobalModals,
    ModioToast,
  },
  setup() {
    const { watchTheme } = useTheming()
    const { setBoundaryVars } = useBoundaries()
    const container = ref(null)
    const mainWrap = ref(null)
    const { setToken, accessTokens, isLoggedIn } = authStore()
    const { setLocalMods } = localModStore()
    const { getSSOInfo } = useSSO()

    const { getPublicGame } = gameStore()
    const game = getPublicGame()

    const requireAccess = computed(
      () => game.value?.status === STATUS_NOT_ACCEPTED && !isLoggedIn.value
    )

    // watch for route changes to
    // set correct theming options
    watchTheme()

    useWindowEvents()

    watch(
      requireAccess,
      (req) => {
        getSSOInfo(game.value)
        if (req) {
          setGlobalModal({ modal: MODAL_LOGIN, data: { noAccess: true } })
        }
      },
      { immediate: true }
    )

    onMounted(async () => {
      if (location.pathname !== '/') {
        location.pathname = '/'
        return
      }

      _setWrapVars()
      resizeContainer()

      // Fetch language first so correct headers are sent
      await fetchLanguageFromSdk()

      fetchTokenFromSdk()
      fetchPortalFromSdk()
      fetchPlatformFromSdk()
      fetchLocalModsFromSdk()

      if (accessTokens.value[GAME_ID] && !isLoggedIn.value) {
        updateLocalStorage()
      }
    })

    async function fetchTokenFromSdk() {
      const token = await sdkInterface.getAuthToken()
      if (token) {
        setToken(token)
        updateLocalStorage()
      }
    }

    async function fetchLocalModsFromSdk() {
      const localMods = await sdkInterface.getLocalMods()
      if (localMods) {
        setLocalMods(localMods)
      }
    }

    async function fetchPortalFromSdk() {
      const portal = await sdkInterface.getPortal()
      if (portal) {
        HEADERS.portal = portal
      }
    }

    async function fetchPlatformFromSdk() {
      const platform = await sdkInterface.getPlatform()
      if (platform) {
        sdkPlatform.value = true
        HEADERS.platform = platform
        getPublicGame(true)
      }
    }

    async function fetchLanguageFromSdk() {
      const lang = await sdkInterface.getLanguage()
      if (lang) {
        webInterface.setLanguage(lang)
      }
    }

    function onResize() {
      if (!container.value) return

      containerHeight.value = container.value.scrollHeight
      sendMessage({
        height: container.value.scrollHeight,
        modioHeight: container.value.scrollHeight,
      })
    }

    function resizeContainer() {
      new ResizeObserver(onResize).observe(container.value)
      setTimeout(onResize, 1000)
    }

    function _setWrapVars() {
      // wait for DOM to refresh for mainWrap to be set
      nextTick(() => {
        setBoundaryVars('main', mainWrap.value.getBoundingClientRect())
      })
    }

    return {
      MIN_PAGE_HEIGHT,
      requireAccess,
      container,
      mainWrap,
    }
  },
}
</script>
